<!-- =====================================================================
     Grey placeholder shapes, chosen by the `id` this component is given.

     Two families of skeleton live here:

       1. The small general-purpose ones (tabs, table-items, form-fields,
          label-input-items) used across the user-management screens. A caller
          normally drops several of these into its own layout.

       2. The Export-DWG (CAD) step skeletons at the bottom. Those are whole-step
          shapes, so a step renders exactly one of them. Which shapes exist, and
          how many panels/cards/fields each draws, is described in
          cad-skeleton.model.ts rather than repeated as markup here.
     ===================================================================== -->
@switch (id) {
  <!-- Tabs Skeleton -->
  @case ('tabs') {
    <div class="flex gap-3 mb-3">
      @for (item of [1, 2]; track item) {
      <p-skeleton width="10rem" height="2rem"></p-skeleton>
      }
    </div>
  }

  <!-- Table Items Skeleton -->
  @case ('table-items') {
    <div class="table-container">
      <p-table [value]="rowsArray">
        <ng-template pTemplate="header">
          <tr>
            @for (column of columns; track $index) {
            <th id="header">
              <p-skeleton width="80%" styleClass="p-datatable-gridlines"></p-skeleton>
            </th>
            }
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-rowIndex="rowIndex">
          <tr>
            @for (column of columns; track $index) {
            <td>
              <p-skeleton width="80%" styleClass="mb-2" [ngStyle]="{ 'opacity': column?.opacity }"></p-skeleton>
            </td>
            }
          </tr>
        </ng-template>
      </p-table>
      @if (paginator) {
      <div class="flex justify-content-between mt-3">
        <p-skeleton width="12rem" styleClass="mb-2"></p-skeleton>
        <p-skeleton width="16rem" styleClass="mb-2"></p-skeleton>
      </div>
      }
    </div>
  }

  <!-- Form Fields Skeleton: one bar, sized by the caller. -->
  @case ('form-fields') {
    <p-skeleton [width]="size?.width || ''" [height]="size?.height || '1rem'" styleClass="mb-2"></p-skeleton>
  }

  <!-- Label Input Items Skeleton: a label bar above an input bar. -->
  @case ('label-input-items') {
    <p-skeleton [width]="(size?.type === 'label' || size?.type === 'both') && size?.width ? size?.width : '6rem'"
      [height]="(size?.type === 'label' || size?.type === 'both') && size?.height ? size?.height : '1rem'"
      styleClass="mb-2" />
    <p-skeleton [width]="(size?.type === 'input' || size?.type === 'both') && size?.width ? size?.width : ''"
      [height]="(size?.type === 'input' || size?.type === 'both') && size?.height ? size?.height : '40px'"
      styleClass="mb-2" />
  }
}

<!-- =====================================================================
     Export-DWG (CAD) step skeletons.

     `cadSpec` is null for every id above, so nothing below is drawn for them.
     ===================================================================== -->
@if (cadSpec; as spec) {
<div class="cad-skeleton">

  <!-- The sticky step heading, with its search box and view switch on the right.
       Only the steps that actually have those controls ask for it. -->
  @if (spec.toolbar) {
  <div class="cad-skeleton__toolbar">
    <p-skeleton width="6rem" height="1.5rem"></p-skeleton>
    <span class="cad-skeleton__spacer"></span>
    <p-skeleton width="14rem" height="2.25rem"></p-skeleton>
    <p-skeleton width="9rem" height="2.25rem"></p-skeleton>
  </div>
  }

  @switch (spec.layout) {

  <!-- One grid of label + input pairs, with no panels around it. -->
  @case ('form') {
  <div class="cad-skeleton__grid" [ngClass]="'cad-skeleton__grid--' + (spec.columns ?? 3)">
    @for (cell of range((spec.rows ?? 3) * (spec.columns ?? 3)); track cell) {
    <div class="cad-skeleton__field">
      <p-skeleton width="5rem" height="0.75rem"></p-skeleton>
      <p-skeleton height="2.5rem"></p-skeleton>
    </div>
    }
  </div>
  }

  <!-- One panel per track or direction. Steps open the first panel and leave the
       rest closed, so only the first panel draws its cards here too. -->
  @case ('accordion') {
  @for (panel of range(spec.panels ?? 3); track panel) {
  <div class="cad-skeleton__panel">
    <div class="cad-skeleton__panel-bar">
      <p-skeleton shape="circle" size="1rem"></p-skeleton>
      <p-skeleton width="11rem" height="1rem"></p-skeleton>
      <span class="cad-skeleton__spacer"></span>
      <p-skeleton width="5rem" height="2rem"></p-skeleton>
    </div>

    @if (panel === 0) {
    <div class="cad-skeleton__panel-body">
      @for (card of range(spec.cards ?? 2); track card) {
      <div class="cad-skeleton__card">
        <!-- The card's own title row: "S.No 1" on the left, delete button right. -->
        <div class="cad-skeleton__card-bar">
          <p-skeleton width="7rem" height="1rem"></p-skeleton>
          <span class="cad-skeleton__spacer"></span>
          <p-skeleton width="2rem" height="2rem"></p-skeleton>
        </div>
        <div class="cad-skeleton__grid" [ngClass]="'cad-skeleton__grid--' + (spec.columns ?? 3)">
          @for (cell of range((spec.rows ?? 2) * (spec.columns ?? 3)); track cell) {
          <div class="cad-skeleton__field">
            <p-skeleton width="5rem" height="0.75rem"></p-skeleton>
            <p-skeleton height="2.5rem"></p-skeleton>
          </div>
          }
        </div>
      </div>
      }
    </div>
    }
  </div>
  }
  }

  <!-- One of the drawing tables: a header strip above evenly spaced body rows. -->
  @case ('table') {
  <div class="cad-skeleton__table">
    <div class="cad-skeleton__table-row cad-skeleton__table-row--head"
      [ngClass]="'cad-skeleton__grid--' + (spec.columns ?? 4)">
      @for (cell of range(spec.columns ?? 4); track cell) {
      <p-skeleton height="1rem"></p-skeleton>
      }
    </div>
    @for (row of range(spec.rows ?? 6); track row) {
    <div class="cad-skeleton__table-row" [ngClass]="'cad-skeleton__grid--' + (spec.columns ?? 4)">
      @for (cell of range(spec.columns ?? 4); track cell) {
      <p-skeleton height="2.25rem"></p-skeleton>
      }
    </div>
    }
  </div>
  }
  }
</div>
}
